import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import api from "api";
import { useParams, Link } from "react-router-dom";
import { formatDate } from "utils";
import { PHOENIX_URL } from "environment";
import useLocalQuery, { Table } from "hooks/use-local-query";
import { Button, Icon, Input } from "semantic-ui-react";

const PER_PAGE_OPTIONS = [
  { value: 25, text: "25" },
  { value: 50, text: "50" },
  { value: 100, text: "100" },
  { value: 250, text: "250" },
];

const ImportLogs = () => {
  const { importId } = useParams();
  const [searchText, setSearchText] = useState("");

  const importLogs = useQuery({
    queryKey: [`import-logs`, importId],
    queryFn: async () => {
      const response = await api.get(`/api/importhistory/${importId}`);
      return response.data;
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 15000;
      const status = data?.status;
      if (status === "Completed" || status === "Error") return false;
      return 15000;
    },
  });

  const downloadMutation = useMutation({
    mutationFn: async (fileName) => {
      const response = await api({
        url: "/api/importhistory/download",
        method: "POST",
        responseType: "blob", // Important
        data: { fileName },
      });
      return response;
    },
    onSuccess: (response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", importLogs?.data?.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    // onError: (error) => toast.error(error.message),
  });
  const filteredLogs = useMemo(() => {
    const logs = importLogs?.data?.logs ?? [];
    if (!searchText.trim()) return logs;
    const lower = searchText.toLowerCase();
    return logs.filter((log) => {
      const identifier = log.patient_account || log.order_invoice_number || log.insurance_payor_id || log.insurance_name || "";
      return identifier.toLowerCase().includes(lower);
    });
  }, [importLogs?.data?.logs, searchText]);

  const { rows, query, queryDispatcher, count } = useLocalQuery(
    filteredLogs,
    {
      sort: {},
      filters: {},
      pagination: { page: 1, per: 25 },
    }
  );

  const GetAccountURL = (row) => {
    switch (importLogs?.data?.import_type) {
      case "Resupply":
      case "Compliance":
      case "Demographics":
      case "Therapy Data":
        return `${PHOENIX_URL}/patients/${row.patient_id}`;
      case "Order":
      case "Tracking":
        return `${PHOENIX_URL}/orders/${row.order_id}`;
      //TODO: Add case statement back in when insurance details page is refactored
      // case "Insurance":
      //   return `${PHOENIX_URL}/companies/${importLogs?.data?.company_id}/profile/insurance?id=${row.insurance_id}`;
      default:
        return `${PHOENIX_URL}/dashboard`;
    }
  };

  const GetTableRowValue = (row) => {
    switch (importLogs?.data?.import_type) {
      case "Resupply":
      case "Compliance":
      case "Demographics":
      case "Therapy Data":
        return row.patient_account;
      case "Tracking":
      case "Order":
        return row.order_invoice_number;
      case "Insurance":
        return importLogs?.data?.uses_payor_id
          ? row.insurance_payor_id
          : row.insurance_name;
      default:
        return "";
    }
  };

  const GetHeaderValue = () => {
    switch (importLogs?.data?.import_type) {
      case "Resupply":
      case "Compliance":
      case "Demographics":
      case "Therapy Data":
        return "Patient Account Number";
      case "Order":
      case "Tracking":
        return "Order Invoice Number";
      case "Insurance":
        return (
          "Insurance " + (importLogs?.data?.uses_payor_id ? "Payor ID" : "Name")
        );
      default:
        return "";
    }
  };

  const GetFieldValue = () => {
    switch (importLogs?.data?.import_type) {
      case "Resupply":
      case "Compliance":
      case "Demographics":
      case "Therapy Data":
        return "patient_account";
      case "Order":
      case "Tracking":
        return "order_invoice_number";
      case "Insurance":
        return "insurance_payor_id";
      default:
        return "";
    }
  };

  return (
    <div>
      <div
        style={{
          margin: 5,
          border: "1px solid #ccc",
        }}
      >
        <div
          style={{
            display: "grid",
            background: "#ddd",
            padding: 10,
            fontSize: 20,
            gridTemplateColumns: "repeat(3, 1fr) auto",
          }}
        >
          <div style={{ fontWeight: "bold" }}>
            Company:{" "}
            <span
              style={{
                paddingLeft: 10,
                color: "#118bcb",
              }}
            >
              <Link to={`/companies/${importLogs?.data?.company_id}`}>
                {importLogs?.data?.company_name}
              </Link>
            </span>
          </div>
          <div style={{ fontWeight: "bold" }}>
            Importer Type:{" "}
            <span
              style={{
                paddingLeft: 10,
                color: "#118bcb",
              }}
            >
              {importLogs?.data?.import_type}
            </span>
          </div>
          <div style={{ fontWeight: "bold" }}>
            Status:{" "}
            <span
              style={{
                paddingLeft: 10,
                color: "#118bcb",
              }}
            >
              {importLogs?.data?.status}
            </span>
          </div>
          <div>
            <button onClick={importLogs.refetch}> Refresh</button>
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            padding: 15,
          }}
        >
          <div style={{ fontWeight: "bold" }}>File name</div>
          <div style={{ fontWeight: "bold" }}>Start Time</div>
          <div style={{ fontWeight: "bold" }}>Started By</div>
          <div style={{ fontWeight: "bold" }}>Finish Time</div>
          <div>
            <span style={{ marginRight: 10 }}>
              {importLogs?.data?.file_name}
            </span>

            <Button
              icon
              size="tiny"
              onClick={() => {
                downloadMutation.mutate(importLogs?.data?.file_name);
              }}
            >
              <Icon name="download" />
            </Button>
          </div>
          <div>{formatDate(importLogs?.data?.date_started)}</div>
          <div>{importLogs?.data?.started_by_name}</div>
          <div>{formatDate(importLogs?.data?.date_finished)}</div>
        </div>
        <hr />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            padding: 15,
          }}
        >
          <div style={{ fontWeight: "bold" }}>
            Total {importLogs?.data?.import_type}
          </div>
          <div style={{ fontWeight: "bold" }}>
            Processed {importLogs?.data?.import_type}
          </div>
          <div
            style={{
              fontWeight: "bold",
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              gridGap: 5,
            }}
          >
            <span style={{ color: "red" }}>Errors</span>
            <span style={{ color: "rgb(36, 159, 148)" }}>Created</span>
            <span style={{ color: "rgb(66, 129, 166)" }}>Updated</span>
            <span style={{ color: "#999" }}>Excluded</span>
          </div>

          <div>{importLogs?.data?.count}</div>
          <div>{importLogs?.data?.proccessed_count}</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              justifyContent: "center",
              alignItems: "center",
              gridGap: 5,
            }}
          >
            <span style={{ color: "red" }}>
              {" "}
              {importLogs?.data?.error_count}
            </span>
            <span style={{ color: "rgb(36, 159, 148)" }}>
              {importLogs?.data?.created_count}{" "}
            </span>
            <span style={{ color: "rgb(66, 129, 166)" }}>
              {importLogs?.data?.updated_count}{" "}
            </span>
            <span style={{ color: "#999" }}>
              {" "}
              {importLogs?.data?.excluded_count}
            </span>
          </div>
        </div>
      </div>
      <div
        style={{
          padding: "10px 20px",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <Input
          placeholder={`Search ${GetHeaderValue()}...`}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          icon="search"
          iconPosition="left"
        />
        {searchText && (
          <Button size="mini" onClick={() => setSearchText("")}>Clear</Button>
        )}
      </div>
      <div
        style={{
          padding: 20,
          display: "flex",
          flex: 1,
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Table
          query={query}
          queryDispatcher={queryDispatcher}
          rows={rows}
          rowKey="id"
          usePagination
          perPageOptions={PER_PAGE_OPTIONS}
          count={count}
          TableRow={(row) => (
            <tr>
              <td>
                {/* TODO: Remove ternary and change insurance importer type to hyperlink when insurance details page is refactored */}
                {importLogs?.data?.import_type !== "Insurance" ? (
                  <a href={GetAccountURL(row)} target="_blank" rel="noreferrer">
                    {GetTableRowValue(row)}
                  </a>
                ) : (
                  <span>{GetTableRowValue(row)}</span>
                )}
              </td>

              <td>{row.status}</td>
              <td>{row.error_msg}</td>
              <td>{row.note}</td>
            </tr>
          )}
          columns={[
            {
              header: GetHeaderValue(),
              field: GetFieldValue(),
            },
            {
              header: "Status",
              field: "status",
              filters: [
                { text: "Created", value: "Created" },
                { text: "Updated", value: "Updated" },
                { text: "Excluded", value: "Excluded" },
                { text: "Error", value: "Error" },
              ],
            },
            {
              header: "Error",
              field: "error_msg",
            },
            {
              header: "Note",
              field: "row.note",
            },
          ]}
        />
      </div>
    </div>
  );
};

export default ImportLogs;
